<script setup lang="ts">
import type { AgentOnPage } from '@@/shared/agent/api-types';

defineProps<{
  section: string
  agent: AgentOnPage
}>();

const displayContactForm = defineModel<boolean>();

// https://css-tricks.com/how-to-detect-when-a-sticky-element-gets-pinned/
// NOTE: top position is at -1px to trigger the intersection observer
const navigation = ref<HTMLDivElement>();
const isStuck = ref(false);
useIntersectionObserver(navigation, (entries) => {
  const entry = entries[0];
  if (!entry) return isStuck.value = false;
  isStuck.value = entry.intersectionRatio < 1;
}, { threshold: [1] });
</script>

<template>
  <IadWrapper
    ref="navigation"
    tag="div"
    class="sticky top-[-1px] bg-white h-header lg:h-20 z-10"
    :class="{
      'transition-shadow': true,
      'shadow-[0_4px_3px_0_rgba(0,0,0,0.2)]': isStuck,
    }"
    :inner-classes="{
      'text-lg': true,
      'border-b border-neutral-70': !isStuck,
    }"
  >
    <div class="lg:flex justify-between items-center -mx-m lg:mx-0">
      <nav class="flex font-semibold h-header lg:h-20 text-sm lg:text-lg">
        <a
          v-if="agent.propertyCount"
          href="#properties"
          class="flex-1 lg:flex-auto lg:px-m flex gap-s items-center justify-center hover:text-primary border-b-[3px] border-transparent hover:border-primary"
          :class="{ '!border-primary text-primary': section === 'properties' }"
        >
          <Icon
            name="iad-icon:home"
            class="size-6 hidden lg:block"
          />
          <span v-text="$t(`agent.navigation.properties`)" />
        </a>
        <a
          href="#about"
          class="flex-1 lg:flex-auto lg:px-m flex gap-s items-center justify-center hover:text-primary border-b-[3px] border-transparent hover:border-primary"
          :class="{
            '!border-primary text-primary': section === 'about',
            'pointer-events-none invisible': !agent.propertyCount && !agent.reviewsCount,
          }"
        >
          <Icon
            name="iad-icon:person"
            class="size-6 hidden lg:block"
          />
          <span v-text="$t(`agent.navigation.about_me`)" />
        </a>
        <a
          v-if="agent.reviewsCount"
          href="#reviews"
          class="flex-1 lg:flex-auto lg:px-m flex gap-s items-center justify-center hover:text-primary border-b-[3px] border-transparent hover:border-orang"
          :class="{ '!border-primary text-primary': section === 'reviews' }"
        >
          <Icon
            name="iad-icon:star-outlined"
            class="size-6 hidden lg:block"
          />
          <span v-text="$t(`agent.navigation.reviews`)" />
        </a>
      </nav>
      <div class="hidden lg:flex items-center gap-m bg-white">
        <AgentAvatarNavigation
          v-if="isStuck"
          :agent
        />
        <span
          v-else
          class="font-semibold text-lg"
          v-text="$t(`agent.navigation.contact_cta`)"
        />
        <IadBtn
          size="large"
          @click="displayContactForm = true"
        >
          <Icon name="iad-icon:mail" />
          {{ $t('agent.navigation.contact_cta_short') }}
        </IadBtn>
        <AgentBtnPhone
          size="large"
          :agent
        />
      </div>
    </div>
  </IadWrapper>
</template>
